import client from './client';

export const getWorkOrderCounts = async () => {
    try {
        const response = await client.get('/admin/workOrders/count');
        return response.data;
    } catch (error) {
        console.error("Error fetching work order counts:", error);
        throw new Error("Failed to fetch work order counts");
    }
};

export const getWorkOrders = async (status?: number, trade?: string) => {
    try {
        const params: Record<string, string | number> = {};
        if (status !== undefined) params.status = status;
        if (trade) params.trade = trade;
        const response = await client.get('/admin/workOrders', { params });
        return response.data;
    } catch (error) {
        console.error("Error fetching work orders:", error);
        throw new Error("Failed to fetch work orders");
    }
};

export const getWorkOrder = async (workOrderID: number) => {
    try {
        const response = await client.get(`/admin/workOrders/${workOrderID}`);
        return response.data;
    } catch (error) {
        console.error("Error fetching work order:", error);
        throw new Error("Failed to fetch work order");
    }
};

export const updateWorkOrderStatus = async (workOrderID: number, toStatus: number, adminEmployeeID: number, notes?: string) => {
    try {
        const response = await client.put(`/admin/workOrders/${workOrderID}/status`, { toStatus, adminEmployeeID, notes });
        return response.data;
    } catch (error) {
        console.error("Error updating work order status:", error);
        throw new Error("Failed to update work order status");
    }
};

export const noChargeWorkOrder = async (workOrderID: number, reason: string) => {
    try {
        const response = await client.put(`/admin/workOrders/${workOrderID}/noCharge`, { reason });
        return response.data;
    } catch (error) {
        console.error("Error marking work order as No Charge:", error);
        throw error;
    }
};

export const voidWorkOrder = async (workOrderID: number, reason: string) => {
    try {
        const response = await client.put(`/admin/workOrders/${workOrderID}/void`, { reason });
        return response.data;
    } catch (error) {
        console.error("Error voiding work order:", error);
        throw error;
    }
};

export const setWorkOrderPONumber = async (workOrderID: number, poNumber: string) => {
    try {
        const response = await client.put(`/admin/workOrders/${workOrderID}/poNumber`, { poNumber });
        return response.data;
    } catch (error) {
        console.error("Error setting work order PO number:", error);
        throw error;
    }
};

export const setWorkOrderDiscount = async (workOrderID: number, discountAmount: number) => {
    try {
        const response = await client.put(`/admin/workOrders/${workOrderID}/discount`, { discountAmount });
        return response.data;
    } catch (error) {
        console.error("Error setting work order discount:", error);
        throw error;
    }
};

/** TAS-442 — flip the billing-only flag. Server rejects if the WO already has hour claims. */
export const setWorkOrderBillingOnly = async (workOrderID: number, billingOnly: boolean) => {
    try {
        const response = await client.put(`/admin/workOrders/${workOrderID}`, { billingOnly });
        return response.data;
    } catch (error) {
        console.error("Error setting work order billing-only flag:", error);
        throw error;
    }
};

export const setWorkOrderInvoiceNumber = async (workOrderID: number, invoiceNumber: string) => {
    try {
        const response = await client.put(`/admin/workOrders/${workOrderID}/invoiceNumber`, { invoiceNumber });
        return response.data;
    } catch (error) {
        console.error("Error setting work order invoice number:", error);
        throw error;
    }
};

export const undoVoidWorkOrder = async (workOrderID: number) => {
    try {
        const response = await client.put(`/admin/workOrders/${workOrderID}/undoVoid`);
        return response.data;
    } catch (error) {
        console.error("Error undoing void on work order:", error);
        throw error;
    }
};

export const undoNoChargeWorkOrder = async (workOrderID: number) => {
    try {
        const response = await client.put(`/admin/workOrders/${workOrderID}/undoNoCharge`);
        return response.data;
    } catch (error) {
        console.error("Error undoing No Charge on work order:", error);
        throw error;
    }
};

/**
 * Remove a customer signature captured in error and reopen the work order for
 * signing. The reason is mandatory — it becomes the audit record in the work
 * order's status history. Requires Manage Work Orders.
 */
export const removeCustomerSignature = async (workOrderID: number, reason: string) => {
    try {
        const response = await client.delete(`/admin/workOrders/${workOrderID}/customerSignature`, {
            data: { reason },
        });
        return response.data;
    } catch (error) {
        console.error("Error removing customer signature on work order:", error);
        throw error;
    }
};

/** TAS-297/299 — pre-fill for the send dialog (To from flagged job contacts, CC = you). */
export const getWorkOrderEmailDefaults = async (workOrderID: number): Promise<{
    to: string[];
    contacts: { name: string; roleTitle: string | null; email: string | null; receivesWorkOrderEmails: boolean }[];
    adminEmail: string | null;
}> => {
    const response = await client.get(`/admin/workOrders/${workOrderID}/emailDefaults`);
    return response.data;
};

export const emailWorkOrderPdf = async (
    workOrderID: number,
    payload: { recipient: string; recipients?: string[]; cc?: string[]; subject?: string; body?: string }
) => {
    try {
        const response = await client.post(`/admin/workOrders/${workOrderID}/emailPdf`, payload);
        return response.data;
    } catch (error) {
        console.error("Error emailing work order PDF:", error);
        throw error;
    }
};

export const sendWorkOrderSignatureRequest = async (
    workOrderID: number,
    payload: { recipient: string }
) => {
    try {
        const response = await client.post(`/admin/workOrders/${workOrderID}/sendSignatureRequest`, payload);
        return response.data;
    } catch (error) {
        console.error("Error sending work order signature request:", error);
        throw error;
    }
};

export const openWorkOrderPdf = async (workOrderID: number) => {
    try {
        const response = await client.get(`/admin/workOrders/${workOrderID}/pdf`, { responseType: 'arraybuffer' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
    } catch (error) {
        console.error("Error opening work order PDF:", error);
        throw new Error("Failed to open PDF");
    }
};

/** Fetch a line-item photo through the authenticated API (the blob container is
 *  private, so raw blobUrls can't be used as <img> sources) and return an
 *  object URL. Callers own the URL and should revoke it when done. */
export const getWorkOrderPhotoObjectUrl = async (workOrderID: number, photoID: number): Promise<string> => {
    try {
        const response = await client.get(`/admin/workOrders/${workOrderID}/photos/${photoID}`, { responseType: 'blob' });
        return window.URL.createObjectURL(response.data);
    } catch (error) {
        console.error("Error fetching work order photo:", error);
        throw new Error("Failed to fetch photo");
    }
};

/** TAS-307 — download the current filtered+sorted list as an Excel workbook. */
export const downloadWorkOrdersExcel = async (workOrderIDs: number[]) => {
    try {
        const response = await client.post(
            '/admin/workOrders/exportExcel',
            { workOrderIDs },
            { responseType: 'arraybuffer' }
        );
        const disposition = response.headers?.['content-disposition'] as string | undefined;
        const fileName = disposition?.match(/filename=([^;]+)/)?.[1]?.trim() || 'WorkOrders.xlsx';
        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Error exporting work orders to Excel:", error);
        throw error;
    }
};

export const downloadCombinedWorkOrderPdf = async (workOrderIDs: number[]) => {
    try {
        const response = await client.post(
            '/admin/workOrders/combinedPdf',
            { workOrderIDs },
            { responseType: 'arraybuffer' }
        );
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'work-orders-combined.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Error downloading combined work order PDF:", error);
        throw new Error("Failed to download combined PDF");
    }
};

export const openWorkOrderPdfVersion = async (workOrderID: number, versionNumber: number) => {
    try {
        const response = await client.get(`/admin/workOrders/${workOrderID}/pdfVersion/${versionNumber}`, { responseType: 'arraybuffer' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
    } catch (error) {
        console.error("Error opening work order PDF version:", error);
        throw new Error("Failed to open PDF version");
    }
};

export const uploadCustomerSignedDoc = async (workOrderID: number, file: File, notes?: string) => {
    const formData = new FormData();
    formData.append("file", file);
    if (notes && notes.trim()) {
        formData.append("notes", notes.trim());
    }
    try {
        const response = await client.post(`/admin/workOrders/${workOrderID}/customerSignedDoc`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data;
    } catch (error) {
        console.error("Error uploading customer signed document:", error);
        throw error;
    }
};

export const openCustomerSignedDoc = async (workOrderID: number, versionNumber: number) => {
    try {
        const response = await client.get(`/admin/workOrders/${workOrderID}/customerSignedDoc/${versionNumber}`, { responseType: 'arraybuffer' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
    } catch (error) {
        console.error("Error opening customer signed document:", error);
        throw new Error("Failed to open document");
    }
};
